/**
 * Advanced → Printers — the whole API of the screen that configures an
 * application's device printing (wizard `print` with `delivery: "device"`).
 *
 * Administration on the PLATFORM host: printer addresses and agent tokens are
 * not part of running the application (the backend refuses `/printers` on a
 * bound domain — see `middleware/domain_scope`). The runtime half is
 * `POST /wizard-print/{project}`, called by the wizard host.
 */

import api from './client'

export type PrinterKind = 'pdf' | 'zpl' | 'escpos'
export type PrinterConnectionType = 'raw9100' | 'cups' | 'ipp' | 'windows'

export interface PrinterConnection {
  type: PrinterConnectionType
  /** raw9100: `host[:port]`; ipp: `ipp://host/ipp/print` (or a bare host). */
  address?: string
  /** cups: the queue name on the agent's machine. */
  queue?: string
  /** windows: the printer's name on the (Windows) agent's machine. */
  printer?: string
}

export interface ProjectPrinter {
  id: number
  name: string
  description: string | null
  kind: PrinterKind
  connection: PrinterConnection
  agent_id: number | null
  agent_name: string | null
  agent_online: boolean
  enabled: boolean
  is_default: boolean
}

export interface PrintAgent {
  id: number
  name: string
  enabled: boolean
  online: boolean
  last_seen_at: string | null
  last_ip: string | null
  version: string | null
  hostname: string | null
  /** An install link was made and the agent has not checked in since. */
  install_pending: boolean
  created_at: string | null
}

export interface PrintersOverview {
  printers: ProjectPrinter[]
  agents: PrintAgent[]
  kinds: PrinterKind[]
  connection_types: PrinterConnectionType[]
}

export interface AgentInstall {
  /** `curl -fsSL … | sudo bash` — valid for an hour, single use. */
  command: string
  url: string
  /** The same install for Windows: one line for an elevated PowerShell. */
  windows_command?: string
  windows_url?: string
  expires_at: string
}

export interface CreatedAgent {
  agent: PrintAgent
  /** Shown ONCE. */
  token: string
  config: Record<string, unknown>
  install: AgentInstall
  systemd_unit: string
}

/** `rendering`: a PDF still being rendered on the server (then `queued`). */
export type PrintJobStatus = 'rendering' | 'queued' | 'sent' | 'printed' | 'failed'

export interface PrintJob {
  id: number
  printer_id: number
  printer_name: string | null
  obj_name: string
  title: string | null
  output_format: string
  copies: number
  status: PrintJobStatus
  error: string | null
  attempts: number
  size: number | null
  source: { client?: string; wizard?: string; run_id?: string } | null
  created_by: string | null
  created_at: string | null
  sent_at: string | null
  finished_at: string | null
  can_retry: boolean
}

export interface PrinterInput {
  name?: string
  description?: string | null
  kind?: PrinterKind
  connection?: PrinterConnection
  agent_id?: number | null
  enabled?: boolean
  is_default?: boolean
}

const base = (project: string) => `/printers/${encodeURIComponent(project)}`

export async function getPrinters(project: string): Promise<PrintersOverview> {
  return (await api.get(base(project))).data
}

export async function addPrinter(project: string, body: PrinterInput): Promise<PrintersOverview> {
  return (await api.post(`${base(project)}/printers`, body)).data
}

export async function updatePrinter(project: string, id: number, body: PrinterInput): Promise<PrintersOverview> {
  return (await api.put(`${base(project)}/printers/${id}`, body)).data
}

export async function deletePrinter(project: string, id: number): Promise<PrintersOverview> {
  return (await api.delete(`${base(project)}/printers/${id}`)).data
}

export async function testPrinter(project: string, id: number): Promise<{ ok: boolean; job: PrintJob }> {
  return (await api.post(`${base(project)}/printers/${id}/test`)).data
}

export async function addAgent(project: string, name: string): Promise<PrintersOverview & { created: CreatedAgent }> {
  return (await api.post(`${base(project)}/agents`, { name })).data
}

export async function updateAgent(project: string, id: number, body: { name?: string; enabled?: boolean }): Promise<PrintersOverview> {
  return (await api.put(`${base(project)}/agents/${id}`, body)).data
}

export async function agentInstallCommand(project: string, id: number): Promise<{ install: AgentInstall; systemd_unit: string }> {
  return (await api.post(`${base(project)}/agents/${id}/install`)).data
}

export async function rotateAgentToken(project: string, id: number): Promise<{ token: string; config: Record<string, unknown> }> {
  return (await api.post(`${base(project)}/agents/${id}/rotate`)).data
}

export async function deleteAgent(project: string, id: number): Promise<PrintersOverview> {
  return (await api.delete(`${base(project)}/agents/${id}`)).data
}

/** The agent itself (`twasta_print_agent.py`), for a manual install. */
export async function downloadAgentFile(project: string): Promise<void> {
  const res = await api.get(`${base(project)}/agent-file`, { responseType: 'blob' })
  const url = URL.createObjectURL(res.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'twasta_print_agent.py'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export async function listPrintJobs(project: string, limit = 100): Promise<PrintJob[]> {
  return (await api.get(`${base(project)}/jobs`, { params: { limit } })).data.jobs
}

export async function retryPrintJob(project: string, id: number): Promise<PrintJob> {
  return (await api.post(`${base(project)}/jobs/${id}/retry`)).data.job
}

// ── the runtime door (wizard `delivery: "device"`) ──────────────────────────

export interface WizardPrintBody {
  obj_name: string
  params?: Record<string, unknown>
  output_format?: string
  copies?: number
  printer?: string
  title?: string
  /** Seconds the server waits for the agent before answering 202. */
  wait?: number
  wizard?: { obj_name: string; run_id: string }
}

export interface WizardPrintAnswer {
  ok: boolean
  job_id: number
  status: PrintJobStatus
  printer: string
  message?: string
}

/** Queue (and briefly wait for) a device print. 200 printed, 202 still on
 *  its way (`rendering` / `queued` / `sent`);
 *  errors (404 not set up, 502 printer error, 403/422) reject as axios does. */
export async function wizardDevicePrint(project: string, body: WizardPrintBody): Promise<WizardPrintAnswer> {
  return (await api.post(`/wizard-print/${encodeURIComponent(project)}`, body)).data
}

/** Poll one device print until the agent reports — anything but `printed`
 *  / `failed` (`rendering` included) is still pending; `printed` resolves,
 *  `failed` rejects with the printer's error, and running past `timeoutMs`
 *  rejects saying it is still queued (the job itself carries on). */
export async function waitForPrintJob(project: string, jobId: number, timeoutMs = 120_000): Promise<PrintJob> {
  const end = Date.now() + timeoutMs
  let delay = 1000
  for (;;) {
    const job: PrintJob = (await api.get(
      `/wizard-print/${encodeURIComponent(project)}/jobs/${jobId}`)).data.job
    if (job.status === 'printed') return job
    if (job.status === 'failed') throw new Error(`${job.printer_name || 'The printer'}: ${job.error || 'the job failed'}`)
    if (Date.now() >= end) {
      throw new Error(`${job.printer_name || 'The printer'} has not printed it yet — the job is still ${job.status}.`)
    }
    await new Promise(res => setTimeout(res, delay))
    delay = Math.min(delay * 1.5, 5000)
  }
}
